import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { useT } from '../lib/i18n';

const ADMIN_EMAIL = 'franco.scioli.13@gmail.com';

interface BlogPost {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  content_es: string;
  content_en: string;
  og_image: string | null;
  published: boolean;
  created_at: string;
}

interface FormState {
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  content_es: string;
  content_en: string;
  og_image: string;
  published: boolean;
}

const EMPTY: FormState = {
  title_es: '', title_en: '', excerpt_es: '', excerpt_en: '',
  content_es: '', content_en: '', og_image: '', published: false,
};

// ── Markdown renderer (sin dependencias) ──────────────────────────────────────

function parseInline(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/).map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**'))
      return <strong key={i} style={{ color: '#e8edf5' }}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*'))
      return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('`') && p.endsWith('`'))
      return (
        <code key={i} style={{
          background: 'rgba(255,255,255,0.07)', padding: '1px 5px',
          borderRadius: 3, fontFamily: 'monospace', fontSize: '0.88em',
        }}>
          {p.slice(1, -1)}
        </code>
      );
    return p;
  });
}

function renderMarkdown(text: string): React.ReactNode {
  if (!text.trim()) return null;
  return (
    <>
      {text.split(/\n\n+/).map((block, i) => {
        const b = block.trim();
        if (!b) return null;

        if (b.startsWith('### '))
          return <h4 key={i} style={mdH4}>{parseInline(b.slice(4))}</h4>;
        if (b.startsWith('## '))
          return <h3 key={i} style={mdH3}>{parseInline(b.slice(3))}</h3>;
        if (b.startsWith('# '))
          return <h2 key={i} style={mdH2}>{parseInline(b.slice(2))}</h2>;
        if (b.startsWith('> '))
          return <blockquote key={i} style={mdQuote}>{parseInline(b.slice(2))}</blockquote>;

        const lines = b.split('\n');
        if (lines.every(l => /^[-*] /.test(l)))
          return (
            <ul key={i} style={mdList}>
              {lines.map((l, j) => <li key={j}>{parseInline(l.slice(2))}</li>)}
            </ul>
          );
        if (lines.every(l => /^\d+\. /.test(l)))
          return (
            <ol key={i} style={mdList}>
              {lines.map((l, j) => <li key={j}>{parseInline(l.replace(/^\d+\. /, ''))}</li>)}
            </ol>
          );

        return (
          <p key={i} style={mdP}>
            {lines.flatMap((line, j) => [
              j > 0 ? <br key={`br${j}`} /> : null,
              ...parseInline(line),
            ]).filter(Boolean)}
          </p>
        );
      })}
    </>
  );
}

// ── Utilidades ────────────────────────────────────────────────────────────────

function generateSlug(titleEs: string): string {
  // Normaliza a NFD y elimina diacríticos (U+0300–U+036F) antes de limpiar
  const clean = titleEs
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return clean + '-' + Date.now().toString(36);
}

function formatDate(iso: string, lang: string): string {
  return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-AR', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function readMinutes(content: string): number {
  return Math.max(1, Math.round(content.split(/\s+/).length / 200));
}

// ── Componente auxiliar ───────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.75rem', color: '#7a8aaa', marginBottom: '0.3rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Estilos compartidos ───────────────────────────────────────────────────────

const inputSt: React.CSSProperties = {
  width: '100%', background: '#151b2a', border: '1px solid #1e2636',
  borderRadius: 6, color: '#e8edf5', padding: '0.5rem 0.75rem',
  fontSize: '0.9rem', boxSizing: 'border-box',
};
const btnBack: React.CSSProperties = {
  background: 'none', border: '1px solid #2a3045', color: '#7a8aaa',
  borderRadius: 6, padding: '0.4rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer',
};
const btnPrimary: React.CSSProperties = {
  background: '#4a9ae0', border: 'none', color: '#fff', borderRadius: 6,
  padding: '0.45rem 1rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  background: 'none', border: '1px solid #2a3045', color: '#7a8aaa',
  borderRadius: 6, padding: '0.4rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer',
};
const tabBtn: React.CSSProperties = {
  background: '#111520', border: '1px solid #1e2636', color: '#7a8aaa',
  borderRadius: 6, padding: '0.35rem 1.1rem', fontSize: '0.82rem',
  fontWeight: 600, cursor: 'pointer',
};
const tabBtnActive: React.CSSProperties = { background: '#4a9ae0', borderColor: '#4a9ae0', color: '#fff' };

const mdH2: React.CSSProperties = { color: '#4a9ae0', fontSize: '1.25rem', margin: '2rem 0 0.5rem' };
const mdH3: React.CSSProperties = { color: '#e8edf5', fontSize: '1.1rem', margin: '1.5rem 0 0.4rem' };
const mdH4: React.CSSProperties = { color: '#e8edf5', fontSize: '1rem', margin: '1.25rem 0 0.35rem' };
const mdP: React.CSSProperties = { margin: '0 0 0.85rem', lineHeight: 1.75, color: '#c8d0e0' };
const mdList: React.CSSProperties = { margin: '0.5rem 0 0.85rem', paddingLeft: '1.5rem', color: '#c8d0e0', lineHeight: 1.75 };
const mdQuote: React.CSSProperties = { borderLeft: '3px solid #4a9ae0', margin: '1rem 0', padding: '0.5rem 1rem', color: '#7a8aaa', fontStyle: 'italic' };

// ── Componente principal ──────────────────────────────────────────────────────

interface Props { user: User | null; }

export function BlogView({ user }: Props) {
  const { t, lang } = useT();
  const isAdmin = user?.email === ADMIN_EMAIL;

  const [posts, setPosts]       = useState<BlogPost[]>([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<BlogPost | null>(null);
  const [form, setForm]         = useState<FormState>(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [formLang, setFormLang] = useState<'es' | 'en'>('es');

  const load = async () => {
    setLoading(true);
    if (!SUPABASE_ENABLED) { setLoading(false); return; }
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false });
    setPosts(data ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormLang('es');
    setShowForm(true);
  };

  const openEdit = (post: BlogPost, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(post);
    setForm({
      title_es: post.title_es, title_en: post.title_en,
      excerpt_es: post.excerpt_es, excerpt_en: post.excerpt_en,
      content_es: post.content_es, content_en: post.content_en,
      og_image: post.og_image ?? '',
      published: post.published,
    });
    setFormLang('es');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title_es.trim() || saving) return;
    setSaving(true);
    const now = new Date().toISOString();
    if (editing) {
      await supabase.from('blog_posts').update({ ...form, updated_at: now }).eq('id', editing.id);
      // Actualizar post seleccionado si está abierto
      if (selected?.id === editing.id) setSelected(p => p ? { ...p, ...form } : p);
    } else {
      await supabase.from('blog_posts').insert({
        ...form,
        slug: generateSlug(form.title_es),
        created_at: now,
        updated_at: now,
      });
    }
    setSaving(false);
    setShowForm(false);
    await load();
  };

  const handleDelete = async (post: BlogPost, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!window.confirm(t('blog.deleteConfirm'))) return;
    await supabase.from('blog_posts').delete().eq('id', post.id);
    if (selected?.id === post.id) setSelected(null);
    await load();
  };

  const postTitle   = (p: BlogPost) => (lang === 'en' && p.title_en)   ? p.title_en   : p.title_es;
  const postExcerpt = (p: BlogPost) => (lang === 'en' && p.excerpt_en) ? p.excerpt_en : p.excerpt_es;
  const postContent = (p: BlogPost) => (lang === 'en' && p.content_en) ? p.content_en : p.content_es;

  // ── Formulario ──────────────────────────────────────────────────────────────
  if (showForm) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <button onClick={() => setShowForm(false)} style={btnBack}>← {t('blog.formCancel')}</button>
          <h2 style={{ margin: 0, color: '#4a9ae0', fontSize: '1.2rem' }}>
            {editing ? t('blog.formTitleEdit') : t('blog.formTitleCreate')}
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {(['es', 'en'] as const).map(l => (
            <button key={l} onClick={() => setFormLang(l)}
              style={{ ...tabBtn, ...(formLang === l ? tabBtnActive : {}) }}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {formLang === 'es' ? (
          <>
            <Field label={t('blog.formTitleEs')}>
              <input style={inputSt} value={form.title_es}
                onChange={e => setForm(f => ({ ...f, title_es: e.target.value }))} />
            </Field>
            <Field label={t('blog.formExcerptEs')}>
              <textarea style={{ ...inputSt, height: 72, resize: 'vertical' }} value={form.excerpt_es}
                onChange={e => setForm(f => ({ ...f, excerpt_es: e.target.value }))} />
            </Field>
            <Field label={t('blog.formContentEs')}>
              <textarea style={{ ...inputSt, height: 340, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                value={form.content_es}
                onChange={e => setForm(f => ({ ...f, content_es: e.target.value }))} />
            </Field>
          </>
        ) : (
          <>
            <Field label={t('blog.formTitleEn')}>
              <input style={inputSt} value={form.title_en}
                onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} />
            </Field>
            <Field label={t('blog.formExcerptEn')}>
              <textarea style={{ ...inputSt, height: 72, resize: 'vertical' }} value={form.excerpt_en}
                onChange={e => setForm(f => ({ ...f, excerpt_en: e.target.value }))} />
            </Field>
            <Field label={t('blog.formContentEn')}>
              <textarea style={{ ...inputSt, height: 340, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                value={form.content_en}
                onChange={e => setForm(f => ({ ...f, content_en: e.target.value }))} />
            </Field>
          </>
        )}

        <Field label={t('blog.formOgImage')}>
          <input style={inputSt} placeholder="https://blog.grindlog.app/images/og-mi-post.png"
            value={form.og_image}
            onChange={e => setForm(f => ({ ...f, og_image: e.target.value }))} />
        </Field>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#7a8aaa', fontSize: '0.875rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.published}
              onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} />
            {t('blog.formPublished')}
          </label>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setShowForm(false)} style={btnSecondary}>{t('blog.formCancel')}</button>
            <button onClick={handleSave}
              disabled={saving || !form.title_es.trim()}
              style={{ ...btnPrimary, opacity: (saving || !form.title_es.trim()) ? 0.5 : 1 }}>
              {saving ? t('blog.saving') : t('blog.formSave')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Vista de post ───────────────────────────────────────────────────────────
  if (selected) {
    const content = postContent(selected);
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button onClick={() => setSelected(null)} style={btnBack}>{t('blog.back')}</button>
          {isAdmin && (
            <>
              <button onClick={() => openEdit(selected)} style={btnSecondary}>{t('blog.editPost')}</button>
              <button onClick={() => handleDelete(selected)}
                style={{ ...btnSecondary, color: '#e05252', borderColor: '#533030' }}>
                {t('blog.deletePost')}
              </button>
            </>
          )}
          {!selected.published && isAdmin && (
            <span style={{ marginLeft: 'auto', background: '#e0a852', color: '#111520', fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
              {t('blog.draft')}
            </span>
          )}
        </div>

        <h1 style={{ color: '#e8edf5', fontSize: '1.65rem', fontWeight: 700, margin: '0 0 0.5rem', lineHeight: 1.3 }}>
          {postTitle(selected)}
        </h1>
        <div style={{ color: '#7a8aaa', fontSize: '0.8rem', marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
          <span>{formatDate(selected.created_at, lang)}</span>
          <span>{readMinutes(content)} {t('blog.minRead')}</span>
        </div>

        <div style={{ fontSize: '0.95rem' }}>
          {renderMarkdown(content)}
        </div>
      </div>
    );
  }

  // ── Lista de posts ──────────────────────────────────────────────────────────
  const visiblePosts = isAdmin ? posts : posts.filter(p => p.published);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: '#4a9ae0', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.3rem' }}>{t('blog.title')}</h2>
          <p style={{ color: '#7a8aaa', margin: 0, fontSize: '0.9rem' }}>{t('blog.subtitle')}</p>
        </div>
        {isAdmin && (
          <button onClick={openCreate} style={btnPrimary}>{t('blog.newPost')}</button>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="dz-spinner" style={{ width: 28, height: 28 }} />
        </div>
      ) : visiblePosts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#7a8aaa', padding: '3rem 1rem', background: '#111520', border: '1px solid #1e2636', borderRadius: 10 }}>
          {t('blog.empty')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {visiblePosts.map(post => (
            <article key={post.id}
              onClick={() => setSelected(post)}
              style={{ background: '#111520', border: '1px solid #1e2636', borderRadius: 10, padding: '1.25rem 1.5rem', cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = '#4a9ae0')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = '#1e2636')}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                <h3 style={{ color: '#e8edf5', margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 600 }}>
                  {postTitle(post)}
                </h3>
                {!post.published && isAdmin && (
                  <span style={{ flexShrink: 0, background: '#e0a852', color: '#111520', fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>
                    {t('blog.draft')}
                  </span>
                )}
              </div>

              {postExcerpt(post) && (
                <p style={{ color: '#7a8aaa', margin: '0 0 0.75rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                  {postExcerpt(post)}
                </p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={{ color: '#7a8aaa', fontSize: '0.78rem', display: 'flex', gap: '0.75rem' }}>
                  <span>{formatDate(post.created_at, lang)}</span>
                  <span>{readMinutes(postContent(post))} {t('blog.minRead')}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {isAdmin && (
                    <>
                      <button onClick={e => openEdit(post, e)}
                        style={{ ...btnSecondary, fontSize: '0.72rem', padding: '3px 9px' }}>
                        {t('blog.editPost')}
                      </button>
                      <button onClick={e => handleDelete(post, e)}
                        style={{ ...btnSecondary, fontSize: '0.72rem', padding: '3px 9px', color: '#e05252', borderColor: '#533030' }}>
                        {t('blog.deletePost')}
                      </button>
                    </>
                  )}
                  <span style={{ color: '#4a9ae0', fontSize: '0.8rem', fontWeight: 500 }}>
                    {t('blog.readMore')} →
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
